import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import styled from "styled-components";
import { Person, AutoMode } from "@mui/icons-material";
import { TestResult, TestStep } from "../types";
import {
  FileMetadata,
  OperationLog,
  OperationStep,
  getStepJudgment,
  getStepJudgmentDisplay,
} from "../services/api";
import type { TestTarget } from "../services/api";
import {
  getJudgmentColor,
  getJudgmentBackgroundColor,
} from "../utils/judgmentUtils";
import {
  isRegressionTest,
  RegressionThresholds,
} from "../utils/regressionEvidence";
import { RegressionDiffView } from "./RegressionDiffView";
import { ZoomableImageModal } from "./ZoomableImageModal";
import type { BaselineUrlMap } from "../hooks/useBaselineUrls";

interface EvidenceViewerProps {
  testResult: TestResult;
  fileMetadata?: FileMetadata[];
  loadingFiles?: boolean;
  operationLog?: OperationLog | null;
  evidenceFiles?: Record<string, string>;
  loadingOperationLog?: boolean;
  loadingEvidenceFiles?: boolean;
  humanJudgment: string | null;
  onInterpretationUpdate: (
    testResultId: number,
    interpretationCorrect: boolean
  ) => Promise<void>;
  apiTestResult?: any; // APIから取得した詳細データ
  projectStatusIds?: any; // プロジェクトのステータスID定義
  template?: any; // テンプレート情報
  stepJudgments?: { [stepOrder: number]: string }; // ステップごとの判定状態
  onStepJudgmentChange?: (stepOrder: number, judgment: string) => void; // ステップ判定変更ハンドラ
  stepInterpretations?: { [stepOrder: number]: boolean }; // ステップごとのAI解釈正誤状態
  onStepInterpretationUpdate?: (stepOrder: number, correct: boolean) => void; // ステップ解釈更新ハンドラ
  initialStepIndex?: number; // 初期表示するステップインデックス
  testTargets?: TestTarget[]; // リグレッション時のマスク情報 lookup 用
  baselineUrls?: BaselineUrlMap; // リグレッション時の baseline 画像 URL（{order: {full?, target?}}）
  regressionBaseline?: Record<string, { full?: string | null; target?: string | null } | null>; // controller が書く marker（空 dict でも有効。1 回目実行判定に使う）
  regressionThresholds?: RegressionThresholds; // プロジェクト固有の MS-SSIM 閾値（未指定時はデフォルトを使用）
}

const ViewerContainer = styled.div`
  display: flex;
  flex-direction: column;
  /* 以前は height: calc(100vh - 80px) だったが、上部クロム（HeaderControls +
     TestHeader）の実高さが 80px を超えると下端がビューポート外にはみ出し、
     最下部の SSIM/判定パネルが見切れていた（tfact_user_request#253）。
     親 MainContent（flex column / overflow:hidden）の残り空間に追従させる。 */
  flex: 1;
  min-height: 0;
  background-color: #f8f9fa;
`;

const StepperContainer = styled.div`
  background-color: #ffffff;
  border-bottom: 1px solid #e0e0e0;
  padding: 8px 12px;
  overflow-x: auto;
  flex-shrink: 0;
`;

const StepperWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: fit-content;
`;

const StepperItem = styled.div<{
  isSelected: boolean;
  hasError: boolean;
  isCompleted: boolean;
  hasEvidence: boolean;
  isWarning: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 6px 4px;
  transition: all 0.2s;
  width: 80px;
  position: relative;

  &:hover {
    transform: translateY(-1px);
  }
`;

const StepNumber = styled.div<{
  hasError: boolean;
  isCompleted: boolean;
  isSelected: boolean;
  hasEvidence: boolean;
  isWarning: boolean;
}>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: white;
  margin-bottom: 4px;
  position: relative;
  box-shadow: 0 1px 4px rgba(0,0,0,0.1);
  
  background-color: ${(props) => {
    if (props.hasError) return "#f44336";
    if (props.isWarning) return "#ff9800";
    if (props.isCompleted) return "#4caf50";
    if (props.isSelected) return "#2196f3";
    return "#9e9e9e";
  }};
  
  ${(props) =>
    props.isSelected &&
    `
    box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.3);
  `}
  
  ${(props) =>
    props.hasEvidence &&
    `
    &::after {
      content: "📸";
      position: absolute;
      top: -4px;
      right: -4px;
      font-size: 12px;
      background: linear-gradient(135deg, #ff5722, #d32f2f);
      border: 2px solid white;
      border-radius: 50%;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      box-shadow: 0 2px 8px rgba(211, 47, 47, 0.4);
      animation: pulse 2s infinite;
    }
    
    @keyframes pulse {
      0% {
        box-shadow: 0 2px 8px rgba(211, 47, 47, 0.4), 0 0 0 0 rgba(211, 47, 47, 0.7);
      }
      70% {
        box-shadow: 0 2px 8px rgba(211, 47, 47, 0.4), 0 0 0 6px rgba(211, 47, 47, 0);
      }
      100% {
        box-shadow: 0 2px 8px rgba(211, 47, 47, 0.4), 0 0 0 0 rgba(211, 47, 47, 0);
      }
    }
  `}
`;

const StepLabel = styled.div<{
  isSelected: boolean;
  hasError: boolean;
  isWarning: boolean;
}>`
  font-size: 10px;
  color: ${(props) => {
    if (props.isSelected) return "#2196f3";
    if (props.hasError) return "#f44336";
    if (props.isWarning) return "#ff9800";
    return "#2c3e50";
  }};
  text-align: center;
  line-height: 1.1;
  max-width: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: ${(props) => (props.isSelected ? "600" : "500")};
`;

const StepConnector = styled.div<{ isCompleted: boolean }>`
  height: 2px;
  width: 32px;
  background-color: ${(props) => (props.isCompleted ? "#4caf50" : "#e0e0e0")};
  margin: 0 4px;
  flex-shrink: 0;
`;

const StepperSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 12px;
  color: #5a6268;
  flex-wrap: wrap;
`;

const OperationTypeCount = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const StepOperationTypeIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 2px;
  flex-shrink: 0;
`;

const EvidencePanel = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
`;

const EvidenceHeader = styled.div`
  padding: 8px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f8f9fa;
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const EvidenceTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  color: #2c3e50;
  flex: 1;
`;

const HeaderInfoSection = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const HeaderInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const HeaderInfoLabel = styled.span`
  font-size: 10px;
  color: #6c757d;
  font-weight: 500;
`;

const HeaderInfoValue = styled.span<{ isError?: boolean }>`
  font-size: 11px;
  color: ${(props) => (props.isError ? "#dc3545" : "#495057")};
  font-weight: 600;
`;

const FileSection = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #fafbfc;
  flex-shrink: 0;
`;

const FileSectionTitle = styled.h4`
  margin: 0 0 6px 0;
  font-size: 12px;
  color: #495057;
  font-weight: 600;
`;

const FileList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 4px;
  max-height: 80px;
  overflow-y: auto;
`;

const FileItem = styled.div`
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 4px 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: #495057;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const FileIcon = styled.span`
  font-size: 12px;
  flex-shrink: 0;
`;

const FileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const FileName = styled.span`
  font-weight: 500;
  color: #212529;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 10px;
`;

const FileSize = styled.span`
  font-size: 9px;
  color: #6c757d;
`;

const LoadingIndicator = styled.div`
  color: #6c757d;
  font-size: 12px;
  font-style: italic;
`;

const EvidenceContent = styled.div`
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// 新しいスタイルコンポーネント
const CompactInfoSection = styled.div`
  background-color: #f8f9fa;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  margin-bottom: 16px;
`;

const CompactInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
`;

const CompactInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CompactInfoLabel = styled.span`
  font-size: 10px;
  color: #6c757d;
  font-weight: 500;
`;

const CompactInfoValue = styled.span<{ isError?: boolean }>`
  font-size: 11px;
  color: ${(props) => (props.isError ? "#dc3545" : "#495057")};
  font-weight: 500;
`;

const MainScoreSection = styled.div`
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
`;

const ScoreHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const ScoreBadge = styled.div`
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 16px;
  font-weight: 700;
  color: #2c3e50;
  background-color: #e9ecef;
  border: 2px solid #6c757d;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ScoreLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #6c757d;
  text-transform: uppercase;
`;

const ResultBadge = styled.div<{
  result: "success" | "warning" | "failure" | "not_evaluated";
}>`
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background-color: ${(props) => {
    switch (props.result) {
      case "success":
        return "#4caf50";
      case "warning":
        return "#ff9800";
      case "failure":
        return "#f44336";
      case "not_evaluated":
        return "#9e9e9e";
      default:
        return "#9e9e9e";
    }
  }};
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const ScoreTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  color: #2c3e50;
  font-weight: 600;
`;

const ReasonSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ReasonItem = styled.div`
  background-color: #f8f9fa;
  border-left: 3px solid #2196f3;
  padding: 8px 12px;
  border-radius: 4px;
`;

const ReasonLabel = styled.div`
  font-size: 12px;
  color: #6c757d;
  font-weight: 600;
  margin-bottom: 4px;
  text-transform: uppercase;
`;

const ReasonText = styled.div`
  font-size: 13px;
  color: #2c3e50;
  line-height: 1.4;
`;

const ToggleSection = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
`;

const ToggleHeader = styled.div`
  padding: 12px 16px;
  background-color: #f8f9fa;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 500;
  color: #495057;
  
  &:hover {
    background-color: #e9ecef;
  }
`;

const ToggleIcon = styled.span<{ isOpen: boolean }>`
  transform: ${(props) => (props.isOpen ? "rotate(90deg)" : "rotate(0deg)")};
  transition: transform 0.2s;
`;

const ToggleContent = styled.div<{ isOpen: boolean }>`
  display: ${(props) => (props.isOpen ? "block" : "none")};
  padding: 16px;
  background-color: #ffffff;
`;

const ActionText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #2c3e50;
  line-height: 1.4;
`;

const StepInfoSection = styled.div`
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
`;

const StepInfoTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #495057;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StepInfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StepInfoItem = styled.div`
  background-color: #f8f9fa;
  border-left: 3px solid #2196f3;
  padding: 8px 12px;
  border-radius: 4px;
`;

const StepInfoLabel = styled.div`
  font-size: 11px;
  color: #6c757d;
  font-weight: 600;
  margin-bottom: 4px;
  text-transform: uppercase;
`;

const StepInfoText = styled.div`
  font-size: 12px;
  color: #2c3e50;
  line-height: 1.4;
  white-space: pre-wrap;
`;

const ContentGrid = styled.div<{ singleColumn?: boolean }>`
  display: grid;
  grid-template-columns: ${(p) => (p.singleColumn ? "1fr" : "2fr 1fr")};
  gap: 16px;
  flex: 1;
  align-items: start;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const VideoSection = styled.div`
  display: flex;
  flex-direction: column;
  height: fit-content;
`;

const SectionTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const VideoTabContainer = styled.div`
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  background-color: #fff;
`;

const VideoTabList = styled.div`
  display: flex;
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
`;

const VideoTab = styled.button<{ active: boolean }>`
  padding: 8px 12px;
  border: none;
  background-color: ${(props) => (props.active ? "#fff" : "transparent")};
  color: ${(props) => (props.active ? "#2196f3" : "#6c757d")};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border-bottom: ${(props) => (props.active ? "2px solid #2196f3" : "none")};
  transition: all 0.2s;

  &:hover {
    background-color: ${(props) => (props.active ? "#fff" : "#e9ecef")};
    color: ${(props) => (props.active ? "#2196f3" : "#495057")};
  }

  &:not(:last-child) {
    border-right: 1px solid #dee2e6;
  }
`;

const VideoTabContent = styled.div`
  background-color: #000;
  position: relative;
  min-height: 300px;
`;

const VideoContainer = styled.div`
  background-color: #000;
  margin-bottom: 8px;
  position: relative;
  min-height: 300px;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const VideoLoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(248, 249, 250, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #495057;
  font-size: 14px;
  z-index: 10;
  border-radius: 8px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(108, 117, 125, 0.3);
  border-top: 3px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 12px;
  color: #6c757d;
  text-align: center;
  font-weight: 500;
`;

const VideoControls = styled.div`
  padding: 8px 12px;
  background-color: #f8f9fa;
  font-size: 11px;
  color: #6c757d;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const EvidenceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TestTargetSection = styled.div`
  margin-top: 16px;
  padding: 16px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
`;

const TestTargetTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  color: #495057;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TestTargetInfo = styled.div`
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 12px;
`;

const TestTargetItem = styled.div`
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TestTargetLabel = styled.span`
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
  display: inline-block;
  min-width: 80px;
`;

const TestTargetValue = styled.span`
  font-size: 12px;
  color: #495057;
  font-weight: 400;
  white-space: pre-wrap;
`;

const TestTargetDescription = styled.div`
  margin-top: 8px;
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  font-size: 11px;
  white-space: pre-wrap;
  color: #6c757d;
  line-height: 1.4;
`;

const TestTargetParams = styled.pre`
  margin: 0;
  padding: 8px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 11px;
  color: #495057;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
`;

const RightPanel = styled.div<{ singleColumn?: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  height: fit-content;
  /* 2 カラム（動画 + 右パネル）では右パネルを sticky で固定し、内部 TabContent を
     スクロールさせる。ただし単一カラム（リグレッション/動画なし）では並走する
     左カラムが無く sticky の意味がない上、vh ベースの max-height が上部クロムの
     実高さを過小評価して最下部の SSIM/判定パネルを画面外へ押し出す
     （tfact_user_request#253）。単一カラム時は自然フローにし、スクロールは親
     EvidencePanel に委ねることで最下部まで必ず到達できるようにする。 */
  ${(p) =>
    p.singleColumn
      ? ""
      : `
  max-height: calc(100vh - 120px);
  overflow: hidden;
  position: sticky;
  top: 10px;
  `}
`;

const TabContainer = styled.div`
  border-bottom: 1px solid #e9ecef;
  background-color: white;
  border-radius: 8px 8px 0 0;
`;

const TabList = styled.div`
  display: flex;
  padding: 0;
`;

const TabButton = styled.button<{ active: boolean }>`
  padding: 12px 20px;
  border: none;
  background: ${(props) => (props.active ? "#f8f9fa" : "white")};
  color: ${(props) => (props.active ? "#007bff" : "#6c757d")};
  font-size: 14px;
  font-weight: ${(props) => (props.active ? "600" : "500")};
  cursor: pointer;
  border-bottom: ${(props) =>
    props.active ? "2px solid #007bff" : "2px solid transparent"};
  transition: all 0.2s ease;
  border-radius: ${(props) => (props.active ? "8px 8px 0 0" : "0")};
  
  &:hover {
    color: #007bff;
    background: ${(props) => (props.active ? "#f8f9fa" : "#f8f9fa")};
  }
  
  &:first-child {
    border-radius: 8px 0 0 0;
  }
`;

const TabContent = styled.div<{ singleColumn?: boolean }>`
  padding: 12px;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* 単一カラム時は内部スクロール（vh ベース max-height）を持たせず、親
     EvidencePanel の 1 スクロールに統一する（#253 の見切れ対策）。 */
  ${(p) =>
    p.singleColumn
      ? ""
      : `
  overflow-y: auto;
  max-height: calc(100vh - 180px);
  `}
`;

const EvidenceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 120px));
  gap: 8px;
  max-width: 600px;
`;

const EvidenceItem = styled.div`
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.2s;
  width: 100px;
  height: auto;
  position: relative;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  animation: evidencePulse 2s infinite;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    border-color: #007bff;
  }

  @keyframes evidencePulse {
    0% {
      box-shadow: 0 2px 4px rgba(0,0,0,0.1), 0 0 0 0 rgba(211, 47, 47, 0.7);
    }
    70% {
      box-shadow: 0 2px 4px rgba(0,0,0,0.1), 0 0 0 8px rgba(211, 47, 47, 0);
    }
    100% {
      box-shadow: 0 2px 4px rgba(0,0,0,0.1), 0 0 0 0 rgba(211, 47, 47, 0);
    }
  }
`;

const EvidenceImage = styled.img`
  width: 100%;
  height: 80px;
  object-fit: cover;
  aspect-ratio: 1;
`;

const EvidenceLabel = styled.div`
  padding: 4px 6px;
  font-size: 10px;
  color: #6c757d;
  background-color: #f8f9fa;
  text-align: center;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ParametersCode = styled.pre`
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px;
  font-size: 12px;
  overflow-x: auto;
  max-height: 200px;
  margin: 0;
  font-family: 'Monaco', 'Consolas', monospace;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
  font-size: 16px;
`;

// 画像拡縮 Lightbox は ZoomableImageModal に切り出し済み（issue #195）。

const EvidenceIcon = styled.span`
  font-size: 16px;
  color: #6c757d;
`;

const NoEvidenceContainer = styled.div`
  padding: 24px;
  text-align: center;
  color: #6c757d;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  font-size: 13px;
  background-color: #f8f9fa;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const NoEvidenceIcon = styled.div`
  font-size: 32px;
  margin-bottom: 8px;
  color: #adb5bd;
`;

const NoEvidenceText = styled.div`
  font-weight: 500;
  margin-bottom: 4px;
`;

const NoEvidenceSubText = styled.div`
  font-size: 12px;
  color: #868e96;
`;

// エビデンスタブ強調用バッジ
const EvidenceTabBadge = styled.span`
  display: inline-block;
  min-width: 18px;
  padding: 0 6px;
  margin-left: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: #2196f3;
  border-radius: 10px;
  vertical-align: middle;
`;

const EvidenceViewer: React.FC<EvidenceViewerProps> = ({
  testResult,
  fileMetadata,
  loadingFiles,
  operationLog,
  evidenceFiles,
  loadingOperationLog,
  loadingEvidenceFiles,
  humanJudgment,
  onInterpretationUpdate,
  apiTestResult,
  projectStatusIds,
  template,
  stepJudgments,
  onStepJudgmentChange,
  stepInterpretations,
  onStepInterpretationUpdate,
  initialStepIndex = 0,
  testTargets = [],
  baselineUrls,
  regressionBaseline,
  regressionThresholds,
}) => {
  const [selectedStepIndex, setSelectedStepIndex] =
    useState<number>(initialStepIndex);
  // 画像拡大モーダルは ZoomableImageModal が状態管理する。ここでは表示中の URL のみ保持。
  const [modalImage, setModalImage] = useState<string | null>(null);

  // initialStepIndexが変更されたら選択を更新
  useEffect(() => {
    console.log(
      "EvidenceViewer: initialStepIndexが変更されました:",
      initialStepIndex
    );
    setSelectedStepIndex(initialStepIndex);
  }, [initialStepIndex]);

  // selectedStepIndexの変更を監視
  useEffect(() => {
    console.log(
      "EvidenceViewer: selectedStepIndexが更新されました:",
      selectedStepIndex
    );
  }, [selectedStepIndex]);

  // 判定パネル用のstate
  const existingTagAnalysis = testResult.value?.tag_analysis || {};
  const [interpretationCorrect, setInterpretationCorrect] = useState(
    existingTagAnalysis.ai_interpretation_correct !== false
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasBeenJudged, setHasBeenJudged] = useState(
    !!existingTagAnalysis.human_judgment
  );
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [showParameters, setShowParameters] = useState<boolean>(false);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const [selectedVideoTab, setSelectedVideoTab] = useState(0);
  const [activeTab, setActiveTab] = useState<
    "overview" | "details" | "evidence"
  >("evidence");
  const [videoLoadingStates, setVideoLoadingStates] = useState<
    Record<string, boolean>
  >({});
  const videoRef = useRef<HTMLVideoElement>(null);
  const isChangingTimeRef = useRef(false); // 時刻変更中フラグ

  const selectedStep = testResult.steps[selectedStepIndex];

  // activeTab デバッグログ
  console.log("EvidenceViewer activeTab:", activeTab);

  // 動画のローディング状態を管理するヘルパー関数
  const setVideoLoading = useCallback(
    (videoKey: string, isLoading: boolean) => {
      setVideoLoadingStates((prev) => ({
        ...prev,
        [videoKey]: isLoading,
      }));
    },
    []
  );

  const isVideoLoading = useCallback(
    (videoKey: string) => {
      return videoLoadingStates[videoKey] || false;
    },
    [videoLoadingStates]
  );

  // テストケースが変更されたときにステップを初期化
  useEffect(() => {
    setSelectedStepIndex(0);
    setModalImage(null);
    setShowDetails(false);
    setShowParameters(false);
    setImageErrors(new Set());
    setSelectedVideoTab(0);
    setVideoLoadingStates({});
  }, [testResult.id]);

  // ステップが変更されたときにビデオタブをリセット
  useEffect(() => {
    setSelectedVideoTab(0);
  }, [selectedStepIndex]);

  // 初期表示時（selectedStepIndex=0）の動画時間設定
  useEffect(() => {
    if (selectedStepIndex === 0 && operationLog && evidenceFiles) {
      console.log(`初期表示時の動画時間設定を開始します`);

      setTimeout(() => {
        // operationVideos は既にメモ化済み

        if (operationVideos.length === 0) {
          console.log(`初期表示: 動画が存在しません`);
          return;
        }

        // 単一動画の場合
        if (operationVideos.length === 1) {
          if (videoRef.current) {
            const targetTime = calculateVideoTime(0, 0);
            console.log(
              `初期表示: 単一動画の時間を ${targetTime.toFixed(1)}秒 に設定`
            );
            setVideoTime(videoRef.current, targetTime);
          }
          return;
        }

        // 複数動画の場合
        const activeVideoIndex = getActiveVideoForStep(0);
        setSelectedVideoTab(activeVideoIndex);

        // DOM更新を待って動画時間を設定
        setTimeout(() => {
          const videoKey = `video-${testResult.id}-0-${activeVideoIndex}`;
          let targetVideoElement = document.querySelector(
            `video[data-key="${videoKey}"]`
          ) as HTMLVideoElement;

          // 見つからない場合は現在表示されている動画を探す
          if (!targetVideoElement) {
            const allVideos = document.querySelectorAll("video");
            for (let i = 0; i < allVideos.length; i++) {
              const video = allVideos[i] as HTMLVideoElement;
              const rect = video.getBoundingClientRect();
              if (rect.width > 0 && rect.height > 0) {
                targetVideoElement = video;
                break;
              }
            }
          }

          if (targetVideoElement) {
            const targetTime = calculateVideoTime(0, activeVideoIndex);
            console.log(
              `初期表示: 複数動画(タブ${
                activeVideoIndex + 1
              })の時間を ${targetTime.toFixed(1)}秒 に設定`
            );
            setVideoTime(targetVideoElement, targetTime);
          } else {
            console.warn(`初期表示: 動画要素が見つかりません: ${videoKey}`);
          }
        }, 200);
      }, 300);
    }
  }, [selectedStepIndex, operationLog, evidenceFiles, testResult.id]);

  // 画像モーダル系のキーボード / ホイール / ドラッグ操作は ZoomableImageModal が持つ。

  // 動画の時間を設定する共通関数
  const setVideoTime = useCallback(
    (videoElement: HTMLVideoElement, targetTime: number) => {
      console.log(
        `動画時間設定開始: ${targetTime.toFixed(1)}秒, readyState: ${
          videoElement.readyState
        }, src: ${videoElement.src || "なし"}, currentSrc: ${
          videoElement.currentSrc || "なし"
        }`
      );

      // srcまたはcurrentSrcが設定されているかチェック
      const hasValidSrc = videoElement.src && videoElement.src !== "";
      const hasValidCurrentSrc =
        videoElement.currentSrc && videoElement.currentSrc !== "";

      if (!hasValidSrc && !hasValidCurrentSrc) {
        console.warn("動画URLが設定されていないため、時刻設定をスキップします");
        return;
      }

      // 時刻変更中フラグを設定
      isChangingTimeRef.current = true;

      const attemptSetTime = () => {
        if (videoElement.readyState >= 1) {
          // HAVE_METADATA以上
          videoElement.currentTime = targetTime;
          console.log(`動画の時間を ${targetTime.toFixed(1)}秒 に設定完了`);
          // フラグをリセット
          setTimeout(() => {
            isChangingTimeRef.current = false;
          }, 200);
        } else {
          console.log(
            `動画メタデータ読み込み待機中... readyState: ${videoElement.readyState}`
          );

          // メタデータの読み込みを待つ
          const handleLoadedMetadata = () => {
            videoElement.currentTime = targetTime;
            console.log(
              `動画の時間を ${targetTime.toFixed(
                1
              )}秒 に設定完了（メタデータ読み込み後）`
            );
            videoElement.removeEventListener(
              "loadedmetadata",
              handleLoadedMetadata
            );
            // フラグをリセット
            setTimeout(() => {
              isChangingTimeRef.current = false;
            }, 200);
          };

          videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);

          // タイムアウト処理（5秒後に諦める）
          setTimeout(() => {
            videoElement.removeEventListener(
              "loadedmetadata",
              handleLoadedMetadata
            );
            console.warn("動画メタデータの読み込みがタイムアウトしました");
          }, 5000);
        }
      };
      attemptSetTime();
    },
    []
  );

  // ステップ選択時に適切な動画タブを選択
  useEffect(() => {
    // operationVideos は既にメモ化済み

    console.log(`ステップ選択時の動画タブ決定:`, {
      selectedStepIndex: selectedStepIndex + 1,
      operationVideosCount: operationVideos.length,
      currentSelectedVideoTab: selectedVideoTab + 1,
    });

    if (operationVideos.length > 1) {
      // 複数動画がある場合は適切なタブを選択
      const activeVideoIndex = getActiveVideoForStep(selectedStepIndex);
      console.log(
        `ステップ ${selectedStepIndex + 1} で動画タブ ${
          activeVideoIndex + 1
        } を選択 (変更前: ${selectedVideoTab + 1})`
      );
      setSelectedVideoTab(activeVideoIndex);
    } else if (operationVideos.length === 1) {
      console.log(`単一動画のため、タブ変更なし`);
    } else {
      console.log(`動画が存在しないため、タブ変更なし`);
    }

    // 新しいステップの動画のローディング状態を初期化
    if (operationVideos.length === 1) {
      const singleVideoKey = `single-video-${testResult.id}-${
        operationVideos[0]?.file || "unknown"
      }`;
      // URLが有効な場合のみローディング状態を設定
      if (operationVideos[0].url) {
        // 同じ動画ファイルの場合はローディング状態をリセットしない
        if (!videoLoadingStates[singleVideoKey]) {
          setVideoLoading(singleVideoKey, false);
        }
      } else {
        // URLが空の場合はローディング状態を設定しない（手動で制御）
        setVideoLoading(singleVideoKey, false);
      }
    }
  }, [selectedStepIndex, testResult.id, setVideoLoading, evidenceFiles]);

  // 動画タブ変更時のローディング状態初期化
  useEffect(() => {
    // operationVideos は既にメモ化済み
    if (operationVideos.length > 1) {
      const videoKey = `video-${testResult.id}-${selectedVideoTab}-${
        operationVideos[selectedVideoTab]?.file || "unknown"
      }`;
      // URLが有効な場合のみローディング状態を設定
      if (operationVideos[selectedVideoTab]?.url) {
        setVideoLoading(videoKey, true);
      } else {
        setVideoLoading(videoKey, false);
      }
    }
  }, [selectedVideoTab, testResult.id, setVideoLoading, evidenceFiles]);

  // primary_evidence.webm のURL更新を監視してローディング状態を更新
  useEffect(() => {
    // operationVideos は既にメモ化済み

    operationVideos.forEach(
      (
        video: { file: string; start_time: number; url: string },
        index: number
      ) => {
        if (video.file === "[ai_generated]primary_evidence.webm") {
          const hasUrl = video.url && video.url.length > 0;

          if (operationVideos.length === 1) {
            // 単一動画の場合
            const singleVideoKey = `single-video-${testResult.id}-${video.file}`;
            if (hasUrl) {
              console.log(
                "primary_evidence.webm のURLが利用可能になりました - 単一動画"
              );
              setVideoLoading(singleVideoKey, false);
            }
          } else {
            // 複数動画の場合
            const videoKey = `video-${testResult.id}-${selectedStepIndex}-${index}`;
            if (hasUrl) {
              console.log(
                "primary_evidence.webm のURLが利用可能になりました - 複数動画"
              );
              setVideoLoading(videoKey, false);
            }
          }
        }
      }
    );
  }, [evidenceFiles, testResult.id, selectedStepIndex, setVideoLoading]);

  // 動画要素の状態を監視
  useEffect(() => {
    const checkVideoState = () => {
      if (videoRef.current) {
        const video = videoRef.current;
        console.log("Video要素の状態確認:", {
          src: video.src,
          currentSrc: video.currentSrc,
          readyState: video.readyState,
          networkState: video.networkState,
          error: video.error?.message || null,
          hasChildren: video.children.length > 0,
        });
      } else {
        console.log("Video要素が見つかりません (videoRef.current is null)");
      }
    };

    // 少し遅延させて動画要素の作成を待つ
    const timer = setTimeout(checkVideoState, 100);
    return () => clearTimeout(timer);
  }, [operationLog, evidenceFiles, selectedStepIndex]);

  // 動画時間調整（ステップ変更時とタブ変更時）- 最適化版
  useEffect(() => {
    // operationVideos は既にメモ化済み

    if (operationVideos.length === 0) {
      return;
    }

    console.log(
      `動画時間調整開始: ステップ${selectedStepIndex + 1}, タブ${
        selectedVideoTab + 1
      }`
    );

    // 単一動画の場合 - 動画要素を再作成せずに時刻のみ変更
    if (operationVideos.length === 1) {
      if (videoRef.current && operationVideos[0].url) {
        const targetTime = calculateVideoTime(selectedStepIndex, 0);
        console.log(
          `単一動画の時間を ${targetTime.toFixed(1)}秒 に設定 (再ロードなし)`
        );

        // 時刻変更中フラグを設定
        isChangingTimeRef.current = true;

        // 動画が読み込み済みの場合は即座に時刻変更
        if (videoRef.current.readyState >= 1) {
          videoRef.current.currentTime = targetTime;
          console.log(`時刻変更完了: ${targetTime.toFixed(1)}秒`);
          // 短時間後にフラグをリセット
          setTimeout(() => {
            isChangingTimeRef.current = false;
          }, 200);
        } else {
          // まだ読み込み中の場合は読み込み完了を待つ
          const handleLoadedMetadata = () => {
            if (videoRef.current) {
              videoRef.current.currentTime = targetTime;
              console.log(
                `時刻変更完了 (読み込み後): ${targetTime.toFixed(1)}秒`
              );
              videoRef.current.removeEventListener(
                "loadedmetadata",
                handleLoadedMetadata
              );
              // フラグをリセット
              setTimeout(() => {
                isChangingTimeRef.current = false;
              }, 200);
            }
          };
          videoRef.current.addEventListener(
            "loadedmetadata",
            handleLoadedMetadata
          );
        }
      }
      return;
    }

    // 複数動画の場合 - 正しいkey属性を生成（動画ファイル名ベース）
    const videoKey = `video-${testResult.id}-${selectedVideoTab}-${
      operationVideos[selectedVideoTab]?.file || "unknown"
    }`;

    // DOM更新を待つため段階的に試行
    let attemptCount = 0;
    const maxAttempts = 10;

    const findAndSetVideoTime = () => {
      attemptCount++;
      console.log(
        `動画要素検索試行 ${attemptCount}/${maxAttempts}: ${videoKey}`
      );

      // まず正確なkey属性で検索
      let targetVideoElement = document.querySelector(
        `video[data-key="${videoKey}"]`
      ) as HTMLVideoElement;

      // 見つからない場合は、現在表示されている動画を探す
      if (!targetVideoElement) {
        const allVideos = document.querySelectorAll("video");
        console.log(`直接検索失敗、全動画要素から検索: ${allVideos.length}個`);

        // 現在表示されている（visible）動画を探す
        for (let i = 0; i < allVideos.length; i++) {
          const video = allVideos[i] as HTMLVideoElement;
          const rect = video.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            console.log(`表示中の動画を発見: インデックス${i}`);
            targetVideoElement = video;
            break;
          }
        }
      }

      if (targetVideoElement) {
        // 動画のsrcが設定されているかチェック（source子要素も確認）
        const hasValidSrc =
          targetVideoElement.src && targetVideoElement.src !== "";
        const hasValidCurrentSrc =
          targetVideoElement.currentSrc && targetVideoElement.currentSrc !== "";

        // source子要素も確認
        const sourceElements = targetVideoElement.querySelectorAll("source");
        const hasValidSourceSrc =
          sourceElements.length > 0 &&
          Array.from(sourceElements).some(
            (source) => source.src && source.src !== ""
          );

        console.log(
          `動画URL状態チェック: src="${targetVideoElement.src}", currentSrc="${targetVideoElement.currentSrc}", sourceCount=${sourceElements.length}, hasValidSourceSrc=${hasValidSourceSrc}`
        );
        console.log(
          `チェック結果: hasValidSrc=${hasValidSrc}, hasValidCurrentSrc=${hasValidCurrentSrc}, hasValidSourceSrc=${hasValidSourceSrc}`
        );

        if (!hasValidSrc && !hasValidCurrentSrc && !hasValidSourceSrc) {
          console.log(
            `動画URLが未設定のため再試行 (${attemptCount}/${maxAttempts})`
          );

          // URLが未設定の場合は短時間後に再試行
          if (attemptCount < maxAttempts) {
            setTimeout(findAndSetVideoTime, 50);
            return false;
          } else {
            console.warn(
              `動画URLが設定されませんでした（${maxAttempts}回試行）: ${videoKey}`
            );
            return false;
          }
        }

        // sourceタグがある場合、video要素のloadを強制実行してsrcを更新
        if (hasValidSourceSrc && !hasValidSrc && !hasValidCurrentSrc) {
          console.log(
            "source要素が見つかりましたが、video.srcが未設定です。loadを実行します。"
          );
          targetVideoElement.load();

          // load後に少し待機してから再試行
          if (attemptCount < maxAttempts) {
            setTimeout(findAndSetVideoTime, 100);
            return false;
          }
        }

        const targetTime = calculateVideoTime(
          selectedStepIndex,
          selectedVideoTab
        );

        // setVideoTime呼び出し直前に最終チェック
        const finalSrcCheck =
          targetVideoElement.src && targetVideoElement.src !== "";
        const finalCurrentSrcCheck =
          targetVideoElement.currentSrc && targetVideoElement.currentSrc !== "";

        console.log(
          `複数動画(タブ${selectedVideoTab + 1})の時間を ${targetTime.toFixed(
            1
          )}秒 に設定 - 最終チェック: src="${
            targetVideoElement.src
          }", currentSrc="${targetVideoElement.currentSrc}"`
        );

        if (!finalSrcCheck && !finalCurrentSrcCheck) {
          console.warn(
            `setVideoTime呼び出し直前でURLが未設定です。スキップします。`
          );
          if (attemptCount < maxAttempts) {
            setTimeout(findAndSetVideoTime, 100);
            return false;
          } else {
            console.error(`最終的にURLが設定されませんでした: ${videoKey}`);
            return false;
          }
        }

        setVideoTime(targetVideoElement, targetTime);
        return true; // 成功
      } else if (attemptCount < maxAttempts) {
        // 再試行
        setTimeout(findAndSetVideoTime, 100);
        return false;
      } else {
        console.error(
          `動画要素が見つかりません（${maxAttempts}回試行）: ${videoKey}`
        );
        return false;
      }
    };

    const timer = setTimeout(findAndSetVideoTime, 100);

    return () => clearTimeout(timer);
  }, [
    selectedStepIndex,
    selectedVideoTab,
    testResult.id,
    setVideoTime,
    evidenceFiles,
  ]);

  // テストケース変更時に動画要素を強制リロード
  useEffect(() => {
    console.log(`テストケース変更検出: ${testResult.id}`);

    // 単一動画の場合
    if (videoRef.current) {
      console.log("単一動画をリロード");
      const video = videoRef.current;
      video.load(); // 動画を強制的にリロード
    }

    // 複数動画の場合 - すべての動画要素をリロード
    const allVideoElements = document.querySelectorAll("video[data-key]");
    console.log(
      `複数動画要素を検索: ${allVideoElements.length}個見つかりました`
    );

    allVideoElements.forEach((video, index) => {
      const videoElement = video as HTMLVideoElement;
      const dataKey = videoElement.getAttribute("data-key");

      // 現在のテストケースIDを含む動画要素のみリロード
      if (dataKey && dataKey.includes(testResult.id)) {
        console.log(`動画要素 ${index + 1} をリロード: ${dataKey}`);
        videoElement.load();
      }
    });

    // 動画ローディング状態をリセット
    setVideoLoadingStates({});

    // 少し遅延させて確実にリロードを実行
    setTimeout(() => {
      console.log("遅延リロードを実行");
      const delayedVideoElements = document.querySelectorAll("video[data-key]");
      delayedVideoElements.forEach((video) => {
        const videoElement = video as HTMLVideoElement;
        const dataKey = videoElement.getAttribute("data-key");
        if (dataKey && dataKey.includes(testResult.id)) {
          videoElement.load();
        }
      });
    }, 100);
  }, [testResult.id]);

  // ステップクリック時の処理（同じステップでも動画時間をリセット）
  const handleStepClick = useCallback(
    (stepIndex: number) => {
      const currentStepIndex = selectedStepIndex;

      // ステップを選択
      setSelectedStepIndex(stepIndex);

      // 同じステップが選択された場合でも動画時間をリセット
      if (currentStepIndex === stepIndex) {
        console.log(
          `同じステップ ${
            stepIndex + 1
          } が再選択されました。動画時間をリセットします。`
        );

        setTimeout(() => {
          // operationVideos は既にメモ化済み

          if (operationVideos.length === 0) {
            return;
          }

          // 単一動画の場合
          if (operationVideos.length === 1) {
            if (videoRef.current) {
              const targetTime = calculateVideoTime(stepIndex, 0);
              console.log(
                `再選択: 単一動画の時間を ${targetTime.toFixed(1)}秒 にリセット`
              );
              setVideoTime(videoRef.current, targetTime);
            }
            return;
          }

          // 複数動画の場合
          const activeVideoIndex = getActiveVideoForStep(stepIndex);

          // 適切な動画タブを選択（必要に応じて）
          if (selectedVideoTab !== activeVideoIndex) {
            setSelectedVideoTab(activeVideoIndex);
          }

          // DOM更新を待って動画時間を設定
          setTimeout(() => {
            const videoKey = `video-${testResult.id}-${stepIndex}-${activeVideoIndex}`;
            let targetVideoElement = document.querySelector(
              `video[data-key="${videoKey}"]`
            ) as HTMLVideoElement;

            // 見つからない場合は現在表示されている動画を探す
            if (!targetVideoElement) {
              const allVideos = document.querySelectorAll("video");
              for (let i = 0; i < allVideos.length; i++) {
                const video = allVideos[i] as HTMLVideoElement;
                const rect = video.getBoundingClientRect();
                if (rect.width > 0 && rect.height > 0) {
                  targetVideoElement = video;
                  break;
                }
              }
            }

            if (targetVideoElement) {
              const targetTime = calculateVideoTime(
                stepIndex,
                activeVideoIndex
              );
              console.log(
                `再選択: 複数動画(タブ${
                  activeVideoIndex + 1
                })の時間を ${targetTime.toFixed(1)}秒 にリセット`
              );
              setVideoTime(targetVideoElement, targetTime);
            } else {
              console.warn(`再選択: 動画要素が見つかりません: ${videoKey}`);
            }
          }, 100);
        }, 50);
      }
    },
    [selectedStepIndex, selectedVideoTab, testResult.id]
  );

  const closeModal = useCallback(() => {
    setModalImage(null);
  }, []);

  const openModal = useCallback((imageUrl: string) => {
    setModalImage(imageUrl);
  }, []);

  const formatTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    const timeString = date.toLocaleTimeString("ja-JP", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    const milliseconds = date.getMilliseconds().toString().padStart(3, "0");
    return `${timeString}.${milliseconds}`;
  };

  const getStepDuration = (step: TestStep): string => {
    // 操作ログのデータのみを使用
    const stepIndex = testResult.steps.indexOf(step);
    const operationStep = getOperationStep(stepIndex);

    if (!operationStep) return "-";
    if (!operationStep.end_time) return "実行中";

    const duration = operationStep.end_time - operationStep.start_time;
    return `${(duration / 1000).toFixed(1)}秒`;
  };

  const formatScore = (score: number): string => {
    return `${Math.round(score)}`;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const getFileIcon = (mimeType: string): string => {
    if (mimeType.startsWith("image/")) return "🖼️";
    if (mimeType.startsWith("video/")) return "🎥";
    if (mimeType.startsWith("audio/")) return "🎵";
    if (mimeType.includes("pdf")) return "📄";
    if (mimeType.includes("text/")) return "📝";
    return "📄";
  };

  // 操作ログから対応するステップを取得
  const getOperationStep = (stepIndex: number): OperationStep | null => {
    if (
      !operationLog ||
      !operationLog.steps ||
      stepIndex >= operationLog.steps.length
    ) {
      return null;
    }
    return operationLog.steps[stepIndex];
  };

  // operation_type ごとの件数を集計
  const operationTypeCounts = useMemo(() => {
    if (!operationLog?.steps?.length) {
      return { total: 0, auto: 0, manual: 0, other: 0 };
    }
    const counts = { auto: 0, manual: 0, other: 0 };
    for (const step of operationLog.steps) {
      const t = (step.operation_type || "").toLowerCase();
      if (t === "auto") counts.auto += 1;
      else if (t === "manual") counts.manual += 1;
      else counts.other += 1;
    }
    return {
      total: operationLog.steps.length,
      ...counts,
    };
  }, [operationLog?.steps]);

  // 操作ログの動画ファイル情報を取得 (メモ化)
  const operationVideos = useMemo((): Array<{
    file: string;
    start_time: number;
    url: string;
  }> => {
    console.log("getOperationVideos 開始:", {
      hasOperationLog: !!operationLog,
      operationLogVideos: operationLog?.videos?.length || 0,
      hasEvidenceFiles: !!evidenceFiles,
      evidenceFilesCount: evidenceFiles ? Object.keys(evidenceFiles).length : 0,
    });

    if (!operationLog || !operationLog.videos) {
      console.log("operationLog または videos が存在しません");
      return [];
    }

    // evidenceFiles の変更を検知するため、依存性を明確にする
    const currentEvidenceFiles = evidenceFiles || {};

    const primaryEvidenceKey = "[ai_generated]primary_evidence.webm";

    // まず evidenceFiles から primary_evidence.webm をチェック
    const hasPrimaryInEvidenceFiles =
      currentEvidenceFiles && primaryEvidenceKey in currentEvidenceFiles;

    // 次に fileMetadata から primary_evidence.webm をチェック
    const hasPrimaryInFileMetadata =
      fileMetadata &&
      fileMetadata.some((file) => file.file_name === primaryEvidenceKey);

    console.log("primary_evidence.webm チェック:", {
      hasPrimaryInEvidenceFiles,
      hasPrimaryInFileMetadata,
      fileMetadataCount: fileMetadata?.length || 0,
      evidenceFilesKeys: Object.keys(currentEvidenceFiles),
      fileMetadataFiles: fileMetadata?.map((f) => f.file_name) || [],
      evidenceFiles: currentEvidenceFiles,
      primaryEvidenceUrl: currentEvidenceFiles[primaryEvidenceKey],
    });

    // primary_evidence.webm がある場合は、それのみを返す
    if (hasPrimaryInEvidenceFiles || hasPrimaryInFileMetadata) {
      // まず operationLog.videos に primary_evidence.webm があるか確認
      const primaryVideoInLog = operationLog.videos.find(
        (video) => video.file === primaryEvidenceKey
      );

      if (primaryVideoInLog && hasPrimaryInEvidenceFiles) {
        // operationLog にあり、evidenceFiles にもある場合
        const result = [
          {
            ...primaryVideoInLog,
            url: currentEvidenceFiles[primaryEvidenceKey],
          },
        ];
        console.log(
          "primary_evidence.webm を operationLog から返します:",
          result
        );
        return result;
      } else if (hasPrimaryInEvidenceFiles) {
        // operationLog にないが evidenceFiles にある場合
        const firstVideo = operationLog.videos[0];
        if (firstVideo) {
          const result = [
            {
              file: primaryEvidenceKey,
              start_time: firstVideo.start_time,
              url: currentEvidenceFiles[primaryEvidenceKey],
            },
          ];
          console.log(
            "primary_evidence.webm を evidenceFiles から返します:",
            result,
            {
              url: result[0]?.url?.substring(0, 100) + "...",
              urlLength: result[0]?.url?.length || 0,
            }
          );
          return result;
        }
      }

      // fileMetadata にのみ存在する場合は、URLがロードされるまで仮のエントリを返す
      if (hasPrimaryInFileMetadata && !hasPrimaryInEvidenceFiles) {
        console.log(
          "primary_evidence.webm は fileMetadata に存在しますが、URLがまだロードされていません"
        );
        // 仮のエントリを返すことで「動画がありません」の表示を防ぐ
        const firstVideo = operationLog.videos[0];
        if (firstVideo) {
          const result = [
            {
              file: primaryEvidenceKey,
              start_time: firstVideo.start_time,
              url: "", // URLは後でロードされる
            },
          ];
          console.log("primary_evidence.webm の仮エントリを返します:", result);
          return result;
        }
      }
    }

    // primary_evidence.webm がない場合は従来通りの処理
    const result = operationLog.videos
      .map((video) => {
        const url = currentEvidenceFiles[video.file];
        return url
          ? {
              ...video,
              url: url,
            }
          : null;
      })
      .filter(
        (video): video is { file: string; start_time: number; url: string } =>
          video !== null
      );

    console.log("従来動画処理結果:", result);
    return result;
  }, [operationLog, evidenceFiles, fileMetadata]); // 必要な依存関係のみ

  // 指定されたステップで操作対象となる動画を特定
  const getActiveVideoForStep = (stepIndex: number): number => {
    if (operationVideos.length <= 1) {
      return 0; // 動画が1つ以下の場合は最初の動画
    }

    // 操作ログから対応するステップを取得
    const operationStep = getOperationStep(stepIndex);
    if (!operationStep) {
      console.log(`操作ログのステップ ${stepIndex + 1} が見つかりません`);
      return 0;
    }

    const stepStartTime = operationStep.start_time;
    const stepEndTime = operationStep.end_time;

    // ステップの実行時間帯に対応する動画を探す
    let activeVideoIndex = 0;
    let bestMatch = -1;

    operationVideos.forEach(
      (
        video: { file: string; start_time: number; url: string },
        index: number
      ) => {
        // 動画がステップ開始時刻より前に開始されているか確認
        if (video.start_time <= stepStartTime) {
          // 次の動画があるかチェック
          const nextVideo = operationVideos[index + 1];

          if (nextVideo) {
            // 次の動画の開始時刻がステップ終了時刻より後の場合、この動画が対象
            if (nextVideo.start_time > stepEndTime) {
              if (video.start_time > bestMatch) {
                bestMatch = video.start_time;
                activeVideoIndex = index;
              }
            }
          } else {
            // 最後の動画の場合、このステップが含まれていれば対象
            if (video.start_time > bestMatch) {
              bestMatch = video.start_time;
              activeVideoIndex = index;
            }
          }
        }
      }
    );

    console.log(`ステップ ${stepIndex + 1} の操作対象動画:`, {
      stepStartTime: `${stepStartTime}ms`,
      stepEndTime: `${stepEndTime}ms`,
      activeVideoIndex,
      videoStartTime: `${operationVideos[activeVideoIndex]?.start_time}ms`,
      videoFile: operationVideos[activeVideoIndex]?.file,
    });

    return activeVideoIndex;
  };

  // ステップ開始時刻に対応する動画内の時間を計算
  const calculateVideoTime = (
    stepIndex: number,
    videoIndex: number
  ): number => {
    // operationVideos は既にメモ化済み
    const stepStartTime = testResult.steps[stepIndex]?.startTime;

    if (!stepStartTime || !operationVideos[videoIndex]) {
      console.warn(`動画時間計算: 必要なデータが不足`, {
        hasStepStartTime: !!stepStartTime,
        hasVideo: !!operationVideos[videoIndex],
        stepStartTime,
        videoIndex,
        operationVideosCount: operationVideos.length,
      });
      return 0;
    }

    const videoStartTime = operationVideos[videoIndex].start_time;

    // 操作ログから対応するステップの開始時刻を取得
    const operationStep = getOperationStep(stepIndex);
    if (!operationStep) {
      console.warn(`操作ログのステップが見つかりません: ${stepIndex + 1}`);
      return 0;
    }

    // primary_evidence.webm の場合は start_time_primary を使用
    const isPrimaryEvidence =
      operationVideos[videoIndex].file ===
      "[ai_generated]primary_evidence.webm";
    let operationStepStartTime: number;

    if (isPrimaryEvidence && operationStep.start_time_primary !== undefined) {
      // primary_evidence.webm かつ start_time_primary が存在する場合
      operationStepStartTime = operationStep.start_time_primary;
      console.log(
        `primary_evidence.webm 用の時間を使用: start_time_primary = ${operationStepStartTime}ms`
      );
    } else {
      // 通常の動画の場合
      operationStepStartTime = operationStep.start_time;
    }

    // 動画開始時刻（ミリ秒単位）- 両方ともテスト開始からの相対時間
    // 動画開始時刻からの相対時間を計算（ミリ秒単位）
    const relativeTimeMs = Math.max(0, operationStepStartTime - videoStartTime);
    const relativeTimeSeconds = relativeTimeMs / 1000;

    // ステップ開始の1秒前に設定（入力前の状態を確認するため）
    const adjustedTimeSeconds = Math.max(0, relativeTimeSeconds + 0.5);

    console.log(`動画時間計算:`, {
      stepIndex: stepIndex + 1,
      videoIndex: videoIndex + 1,
      testResultStartTime: `${stepStartTime}ms (${formatTime(stepStartTime)})`,
      operationStepStartTime: `${operationStepStartTime}ms`,
      videoStartTime: `${videoStartTime}ms`,
      relativeTimeMs: `${relativeTimeMs}ms`,
      relativeTimeSeconds: `${relativeTimeSeconds.toFixed(1)}秒`,
      videoFile: operationVideos[videoIndex].file,
      isPrimaryEvidence: isPrimaryEvidence,
      usedPrimaryTime:
        isPrimaryEvidence && operationStep.start_time_primary !== undefined,
      calculation: `(${operationStepStartTime} - ${videoStartTime}) / 1000 = ${relativeTimeSeconds.toFixed(
        1
      )}秒`,
    });

    return adjustedTimeSeconds;
  };

  // 指定されたステップにエビデンスファイルが存在するかチェック（操作ログベースのみ）
  const hasEvidenceForStep = (stepIndex: number): boolean => {
    // 操作ログが存在しない場合はエビデンスなし
    if (!operationLog || !evidenceFiles) {
      return false;
    }

    const operationStep = getOperationStep(stepIndex);
    if (!operationStep) {
      return false;
    }

    // 操作ログのevidenceフィールドに指定されたファイルが存在するかチェック
    if (operationStep.evidence && evidenceFiles[operationStep.evidence]) {
      return true;
    }

    return false;
  };

  const renderOperationTypeIcon = (operationType: string | undefined) => {
    const t = (operationType || "").toLowerCase();
    const iconSx = { fontSize: 14, color: "inherit" };
    if (t === "auto") {
      return (
        <StepOperationTypeIcon title="自動実行">
          <AutoMode sx={iconSx} fontSize="small" />
        </StepOperationTypeIcon>
      );
    }
    if (t === "manual") {
      return (
        <StepOperationTypeIcon title="手動実行">
          <Person sx={iconSx} fontSize="small" />
        </StepOperationTypeIcon>
      );
    }
    return null;
  };

  const handleImageError = (
    imageUrl: string,
    e: React.SyntheticEvent<HTMLImageElement>
  ) => {
    const img = e.target as HTMLImageElement;

    // 既にエラーが記録されている場合は何もしない（無限ループ防止）
    if (imageErrors.has(imageUrl)) {
      img.style.display = "none";
      return;
    }

    // エラーを記録
    setImageErrors((prev) => new Set(prev).add(imageUrl));

    // プレースホルダー画像に置き換え（data URLを使用）
    img.src =
      "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDIwMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTIwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik04MCA0MEgxMjBWODBIODBWNDBaIiBmaWxsPSIjREREREREIi8+CjxwYXRoIGQ9Ik05MCA1MEwxMTAgNzBIOTBWNTBaIiBmaWxsPSIjQkJCQkJCIi8+CjxjaXJjbGUgY3g9Ijk1IiBjeT0iNTUiIHI9IjMiIGZpbGw9IiNCQkJCQkIiLz4KPHRleHQgeD0iMTAwIiB5PSI5NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEwIiBmaWxsPSIjOTk5OTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj7nlLvlg4/jgYzjgYLjgorjgb7jgZvjgpM8L3RleHQ+Cjwvc3ZnPgo=";
  };

  return (
    <ViewerContainer>
      <StepperContainer>
        {(operationTypeCounts.total > 0 || testResult.steps.length > 0) && (
          <StepperSummary>
            <span>
              全 {operationTypeCounts.total > 0 ? operationTypeCounts.total : testResult.steps.length} ステップ
            </span>
            {operationTypeCounts.total > 0 && (
              <>
                <OperationTypeCount title="自動実行">
                  <StepOperationTypeIcon>
                    <AutoMode sx={{ fontSize: 14, color: "inherit" }} fontSize="small" />
                  </StepOperationTypeIcon>
                  自動: {operationTypeCounts.auto}
                </OperationTypeCount>
                <OperationTypeCount title="手動実行">
                  <StepOperationTypeIcon>
                    <Person sx={{ fontSize: 14, color: "inherit" }} fontSize="small" />
                  </StepOperationTypeIcon>
                  手動: {operationTypeCounts.manual}
                </OperationTypeCount>
                {operationTypeCounts.other > 0 && (
                  <OperationTypeCount>その他: {operationTypeCounts.other}</OperationTypeCount>
                )}
              </>
            )}
          </StepperSummary>
        )}
        <StepperWrapper>
          {testResult.steps.map((step, index) => (
            <React.Fragment key={index}>
              <StepperItem
                isSelected={selectedStepIndex === index}
                hasError={step.result === "failure"}
                isWarning={step.result === "warning"}
                isCompleted={step.result === "success"}
                hasEvidence={hasEvidenceForStep(index)}
                onClick={() => handleStepClick(index)}
                data-step-index={index}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <StepNumber
                    hasError={step.result === "failure"}
                    isWarning={step.result === "warning"}
                    isCompleted={step.result === "success"}
                    isSelected={selectedStepIndex === index}
                    hasEvidence={hasEvidenceForStep(index)}
                  >
                    {index + 1}
                  </StepNumber>
                  {renderOperationTypeIcon(getOperationStep(index)?.operation_type)}
                </div>
                <StepLabel
                  title={step.action}
                  isSelected={selectedStepIndex === index}
                  hasError={step.result === "failure"}
                  isWarning={step.result === "warning"}
                >
                  {step.label || step.action}
                </StepLabel>
              </StepperItem>
              {index < testResult.steps.length - 1 && (
                <StepConnector isCompleted={step.result === "success"} />
              )}
            </React.Fragment>
          ))}
        </StepperWrapper>
      </StepperContainer>

      <EvidencePanel>
        {selectedStep ? (
          <>
            <EvidenceHeader>
              <EvidenceTitle>
                ステップ {selectedStepIndex + 1}:{" "}
                {selectedStep.label || selectedStep.action}
                {selectedStep.result === "failure" && " (失敗)"}
                {selectedStep.result === "warning" && " (要確認)"}
                {selectedStep.result === "not_evaluated" && " (未評価)"}
              </EvidenceTitle>
              <HeaderInfoSection>
                <HeaderInfoItem>
                  <HeaderInfoLabel>開始時刻</HeaderInfoLabel>
                  <HeaderInfoValue>
                    {(() => {
                      const operationStep = getOperationStep(selectedStepIndex);
                      if (!operationStep) return "-";
                      return `${(operationStep.start_time / 1000).toFixed(
                        1
                      )}秒`;
                    })()}
                  </HeaderInfoValue>
                </HeaderInfoItem>
                <HeaderInfoItem>
                  <HeaderInfoLabel>終了時刻</HeaderInfoLabel>
                  <HeaderInfoValue>
                    {(() => {
                      const operationStep = getOperationStep(selectedStepIndex);
                      if (!operationStep) return "-";
                      return operationStep.end_time
                        ? `${(operationStep.end_time / 1000).toFixed(1)}秒`
                        : "実行中";
                    })()}
                  </HeaderInfoValue>
                </HeaderInfoItem>
                <HeaderInfoItem>
                  <HeaderInfoLabel>実行時間</HeaderInfoLabel>
                  <HeaderInfoValue>
                    {getStepDuration(selectedStep)}
                  </HeaderInfoValue>
                </HeaderInfoItem>
                <HeaderInfoItem>
                  <HeaderInfoLabel>結果</HeaderInfoLabel>
                  <HeaderInfoValue isError={selectedStep.result === "failure"}>
                    {selectedStep.result === "success"
                      ? "成功"
                      : selectedStep.result === "warning"
                      ? "要確認"
                      : selectedStep.result === "not_evaluated"
                      ? "未評価"
                      : "失敗"}
                  </HeaderInfoValue>
                </HeaderInfoItem>
              </HeaderInfoSection>
            </EvidenceHeader>

            <EvidenceContent>
              {/* リグレッションテストでは動画は記録されないため動画セクションごと非表示にし、
                  右パネル（Diff 比較ビュー含む）を全幅表示する。
                  evidenceFiles が読み込み中の間は判定不能なので、誤解を招く
                  「動画を読み込み中…」表示が出ないよう VideoSection ごと隠す。 */}
              {(() => {
                const hideVideoSection =
                  loadingEvidenceFiles ||
                  isRegressionTest(evidenceFiles || {}, regressionBaseline);
                return (
              <ContentGrid singleColumn={hideVideoSection}>
                {!hideVideoSection && (
                <>
                {/* 動画セクション */}
                {(() => {
                  console.log("VideoSection レンダリング開始", { activeTab });
                  return null;
                })()}
                <VideoSection>
                  {(() => {
                    // 操作ログから動画を取得（操作ログベースのみ）
                    // operationVideos は既にメモ化済み

                    console.log("VideoSection 条件分岐チェック:", {
                      operationVideosLength: operationVideos.length,
                      loadingOperationLog,
                      firstVideoFile: operationVideos[0]?.file,
                      firstVideoHasUrl: !!operationVideos[0]?.url,
                    });

                    // 操作ログ JSON 自体の読み込み中
                    if (loadingOperationLog) {
                      console.log("分岐: 操作ログ読み込み中");
                      return (
                        <VideoContainer>
                          <VideoLoadingOverlay>
                            <LoadingSpinner />
                            <LoadingText>操作ログを読み込み中...</LoadingText>
                          </VideoLoadingOverlay>
                        </VideoContainer>
                      );
                    }

                    // 操作ログは取れたがエビデンス URL がまだ取得中で
                    // 動画 URL も未到着の場合は、動画ローディング表示にする
                    // （「動画が登録されていない」のチラつきを防ぐ）。
                    if (
                      loadingEvidenceFiles &&
                      operationVideos.length === 0
                    ) {
                      console.log("分岐: エビデンス読み込み中");
                      return (
                        <VideoContainer>
                          <VideoLoadingOverlay>
                            <LoadingSpinner />
                            <LoadingText>動画を読み込み中...</LoadingText>
                          </VideoLoadingOverlay>
                        </VideoContainer>
                      );
                    }

                    // 動画が存在しない場合
                    if (operationVideos.length === 0) {
                      console.log("分岐: 動画が存在しない");
                      return (
                        <VideoContainer>
                          <VideoLoadingOverlay
                            style={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
                          >
                            <div
                              style={{
                                fontSize: "24px",
                                marginBottom: "8px",
                                color: "#6c757d",
                              }}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 16 16"
                                fill="currentColor"
                              >
                                <path d="M0 3a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2h-1l-2-3h1a1 1 0 001-1V3a1 1 0 00-1-1H2a1 1 0 00-1 1v8a1 1 0 001 1h1l-2 3H2a2 2 0 01-2-2V3z" />
                                <path d="M6.5 4.5A1.5 1.5 0 018 6v4a1.5 1.5 0 01-1.5 1.5h-3A1.5 1.5 0 012 10V6a1.5 1.5 0 011.5-1.5h3z" />
                              </svg>
                            </div>
                            <LoadingText>
                              このステップの動画は登録されていないか、登録処理中の可能性があります。しばらく待ってから再度確認してください。
                            </LoadingText>
                          </VideoLoadingOverlay>
                        </VideoContainer>
                      );
                    }

                    // 複数動画がある場合はタブ表示
                    if (operationVideos.length > 1) {
                      console.log("分岐: 複数動画タブ表示");
                      return (
                        <VideoTabContainer>
                          <VideoTabList>
                            {operationVideos.map(
                              (
                                video: {
                                  file: string;
                                  start_time: number;
                                  url: string;
                                },
                                index: number
                              ) => (
                                <VideoTab
                                  key={index}
                                  active={selectedVideoTab === index}
                                  onClick={() => setSelectedVideoTab(index)}
                                >
                                  動画 {index + 1}
                                  <div
                                    style={{ fontSize: "10px", opacity: 0.8 }}
                                  >
                                    開始: {(video.start_time / 1000).toFixed(1)}
                                    s
                                  </div>
                                </VideoTab>
                              )
                            )}
                          </VideoTabList>
                          <VideoTabContent>
                            {operationVideos[selectedVideoTab] &&
                              (() => {
                                const videoKey = `video-${
                                  testResult.id
                                }-${selectedVideoTab}-${
                                  operationVideos[selectedVideoTab]?.file ||
                                  "unknown"
                                }`;
                                // key は result.id+tab+ファイル名で安定させる。
                                // 署名URLのリフレッシュ(8分毎)では remount せず再生状態を保持する。
                                // 結果切替時の差し替えは result.id 変化＋データ層の
                                // resetSelectedEvidence(evidenceFiles 即時クリア)で担保する (#433)。
                                return (
                                  <>
                                    <Video
                                      key={videoKey}
                                      data-key={videoKey}
                                      controls
                                      preload="metadata"
                                      autoPlay={false}
                                      muted
                                      onLoadStart={() => {
                                        // 時刻変更中でなければローディング状態を設定
                                        if (!isChangingTimeRef.current) {
                                          setVideoLoading(videoKey, true);
                                        }
                                      }}
                                      onCanPlay={() =>
                                        setVideoLoading(videoKey, false)
                                      }
                                      onError={() =>
                                        setVideoLoading(videoKey, false)
                                      }
                                      onLoadedData={() =>
                                        setVideoLoading(videoKey, false)
                                      }
                                    >
                                      <source
                                        src={
                                          operationVideos[selectedVideoTab].url
                                        }
                                        type="video/webm"
                                      />
                                      <source
                                        src={
                                          operationVideos[selectedVideoTab].url
                                        }
                                        type="video/mp4"
                                      />
                                      お使いのブラウザは動画再生に対応していません。
                                    </Video>
                                    {(() => {
                                      const isPrimaryEvidence =
                                        operationVideos[selectedVideoTab]
                                          ?.file ===
                                        "[ai_generated]primary_evidence.webm";
                                      const hasUrl =
                                        operationVideos[selectedVideoTab]
                                          ?.url &&
                                        operationVideos[selectedVideoTab].url
                                          .length > 0;

                                      // primary_evidence の場合は、URLがない時だけローディング表示（時刻変更中は非表示）
                                      // 通常の動画の場合は従来のローディング状態を使用（時刻変更中は非表示）
                                      const showLoading = isPrimaryEvidence
                                        ? !hasUrl && !isChangingTimeRef.current
                                        : isVideoLoading(videoKey) &&
                                          !isChangingTimeRef.current;

                                      return (
                                        showLoading && (
                                          <VideoLoadingOverlay>
                                            <LoadingSpinner />
                                            <LoadingText>
                                              {isPrimaryEvidence && !hasUrl
                                                ? "プライマリ動画を準備中..."
                                                : "動画を読み込み中..."}
                                              <br />
                                              {
                                                operationVideos[
                                                  selectedVideoTab
                                                ]?.file
                                              }
                                            </LoadingText>
                                          </VideoLoadingOverlay>
                                        )
                                      );
                                    })()}
                                  </>
                                );
                              })()}
                            <VideoControls>
                              <span>
                                ファイル名:{" "}
                                {operationVideos[selectedVideoTab]?.file}
                              </span>
                              <span>
                                動画開始時間:{" "}
                                {(
                                  operationVideos[selectedVideoTab]
                                    ?.start_time / 1000
                                ).toFixed(1)}
                                s
                              </span>
                              <span>
                                ステップ開始時間:{" "}
                                {(() => {
                                  const operationStep =
                                    getOperationStep(selectedStepIndex);
                                  return operationStep
                                    ? (operationStep.start_time / 1000).toFixed(
                                        1
                                      ) + "s"
                                    : (selectedStep.startTime / 1000).toFixed(
                                        1
                                      ) + "s";
                                })()}
                              </span>
                              <span>
                                動画内時刻:{" "}
                                {calculateVideoTime(
                                  selectedStepIndex,
                                  selectedVideoTab
                                ).toFixed(1)}
                                s
                              </span>
                            </VideoControls>
                          </VideoTabContent>
                        </VideoTabContainer>
                      );
                    }

                    // 単一動画の場合
                    console.log("分岐: 単一動画表示");
                    return (
                      <VideoContainer
                        key={`container-${testResult.id}-${selectedStepIndex}`}
                      >
                        {(() => {
                          // 動画ファイルに基づくkeyで、ステップごとに再作成されないようにする。
                          // key は result.id+ファイル名で安定させ、署名URLのリフレッシュ(8分毎)では
                          // remount せず再生状態を保持する。結果切替時の差し替えは result.id 変化＋
                          // データ層の resetSelectedEvidence(evidenceFiles 即時クリア)で担保する (#433)。
                          const videoKey = `single-video-${testResult.id}-${
                            operationVideos[0]?.file || "unknown"
                          }`;

                          // 動画要素作成前のデバッグログ
                          console.log("動画要素作成前のoperationVideos:", {
                            videosLength: operationVideos.length,
                            firstVideo: operationVideos[0]
                              ? {
                                  file: operationVideos[0].file,
                                  url:
                                    operationVideos[0].url?.substring(0, 100) +
                                    "...",
                                  urlLength:
                                    operationVideos[0].url?.length || 0,
                                  hasUrl: !!operationVideos[0].url,
                                }
                              : null,
                            videoKey,
                          });

                          return (
                            <>
                              <Video
                                key={videoKey}
                                data-key={videoKey}
                                ref={videoRef}
                                src={operationVideos[0]?.url}
                                controls
                                preload="metadata"
                                autoPlay={false}
                                muted
                                onLoadStart={() => {
                                  console.log("動画読み込み開始 (単一動画):", {
                                    url: operationVideos[0]?.url,
                                    file: operationVideos[0]?.file,
                                    videoKey,
                                    isChangingTime: isChangingTimeRef.current,
                                  });
                                  // 時刻変更中でなければローディング状態を設定
                                  if (!isChangingTimeRef.current) {
                                    setVideoLoading(videoKey, true);
                                  }
                                }}
                                onCanPlay={() =>
                                  setVideoLoading(videoKey, false)
                                }
                                onError={(e) => {
                                  console.error(
                                    "動画読み込みエラー (単一動画):",
                                    e,
                                    {
                                      url: operationVideos[0]?.url,
                                      file: operationVideos[0]?.file,
                                      videoKey,
                                    }
                                  );
                                  setVideoLoading(videoKey, false);
                                }}
                                onLoadedData={() =>
                                  setVideoLoading(videoKey, false)
                                }
                              >
                                お使いのブラウザは動画再生に対応していません。
                              </Video>
                              {(() => {
                                const isPrimaryEvidence =
                                  operationVideos[0]?.file ===
                                  "[ai_generated]primary_evidence.webm";
                                const hasUrl =
                                  operationVideos[0]?.url &&
                                  operationVideos[0].url.length > 0;
                                const isVideoReady =
                                  videoRef.current &&
                                  videoRef.current.readyState >= 1;

                                // ローディング表示の条件を最適化
                                let showLoading = false;
                                if (isPrimaryEvidence) {
                                  // primary_evidence の場合: URLがない時のみ（時刻変更中は非表示）
                                  showLoading =
                                    !hasUrl && !isChangingTimeRef.current;
                                } else {
                                  // 通常動画の場合: ローディング状態かつ動画未準備時のみ（時刻変更中は非表示）
                                  showLoading =
                                    isVideoLoading(videoKey) &&
                                    !isVideoReady &&
                                    !isChangingTimeRef.current;
                                }

                                console.log("ローディング表示判定:", {
                                  isPrimaryEvidence,
                                  hasUrl,
                                  isVideoReady,
                                  isVideoLoadingState: isVideoLoading(videoKey),
                                  isChangingTime: isChangingTimeRef.current,
                                  showLoading,
                                });

                                return (
                                  showLoading && (
                                    <VideoLoadingOverlay>
                                      <LoadingSpinner />
                                      <LoadingText>
                                        {isPrimaryEvidence && !hasUrl
                                          ? "プライマリ動画を準備中..."
                                          : "動画を読み込み中..."}
                                        <br />
                                        {operationVideos[0]?.file}
                                      </LoadingText>
                                    </VideoLoadingOverlay>
                                  )
                                );
                              })()}
                            </>
                          );
                        })()}
                        <VideoControls>
                          <span>ファイル名: {operationVideos[0]?.file}</span>
                          <span>
                            動画開始時間:{" "}
                            {(operationVideos[0]?.start_time / 1000).toFixed(1)}
                            s
                          </span>
                          <span>
                            ステップ開始時間:{" "}
                            {(() => {
                              const operationStep =
                                getOperationStep(selectedStepIndex);
                              return operationStep
                                ? (operationStep.start_time / 1000).toFixed(1) +
                                    "s"
                                : (selectedStep.startTime / 1000).toFixed(1) +
                                    "s";
                            })()}
                          </span>
                          <span>
                            動画内時刻:{" "}
                            {calculateVideoTime(selectedStepIndex, 0).toFixed(
                              1
                            )}
                            s
                          </span>
                        </VideoControls>
                      </VideoContainer>
                    );
                  })()}
                </VideoSection>
                </>
                )}

                {/* 右側パネル: 評価結果とエビデンス画像 */}
                <RightPanel singleColumn={hideVideoSection}>
                  <TabContainer>
                    <TabList>
                      <TabButton
                        active={activeTab === "overview"}
                        onClick={() => setActiveTab("overview")}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          style={{ marginRight: "6px" }}
                        >
                          <path d="M1.75 2A1.75 1.75 0 000 3.75v8.5C0 13.216.784 14 1.75 14h12.5A1.75 1.75 0 0016 12.25v-8.5A1.75 1.75 0 0014.25 2H1.75zM1.5 3.75a.25.25 0 01.25-.25h12.5a.25.25 0 01.25.25v1.085L8.5 8.085 1.5 4.835V3.75zm0 2.662L8.5 9.915l7-3.503v5.838a.25.25 0 01-.25.25H1.75a.25.25 0 01-.25-.25V6.412z" />
                        </svg>
                        評価結果
                      </TabButton>
                      <TabButton
                        active={activeTab === "evidence"}
                        onClick={() => setActiveTab("evidence")}
                        style={
                          hasEvidenceForStep(selectedStepIndex)
                            ? {
                                fontWeight: 700,
                                color: "#2196f3",
                                position: "relative",
                              }
                            : {}
                        }
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          style={{ marginRight: "6px" }}
                        >
                          <path d="M8 9a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                          <path
                            fillRule="evenodd"
                            d="M1.5 3a1.5 1.5 0 011.5-1.5h10A1.5 1.5 0 0114.5 3v10a1.5 1.5 0 01-1.5 1.5H3A1.5 1.5 0 011.5 13V3zM3 3v10h10V3H3z"
                          />
                        </svg>
                        エビデンス
                        {hasEvidenceForStep(selectedStepIndex) && (
                          <EvidenceTabBadge>!</EvidenceTabBadge>
                        )}
                      </TabButton>{" "}
                      <TabButton
                        active={activeTab === "details"}
                        onClick={() => setActiveTab("details")}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          style={{ marginRight: "6px" }}
                        >
                          <path
                            fillRule="evenodd"
                            d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8zm6.5-.25A.75.75 0 017.25 7h1a.75.75 0 01.75.75v2.75h.25a.75.75 0 010 1.5h-2a.75.75 0 010-1.5h.25v-2h-.25a.75.75 0 01-.75-.75zM8 6a1 1 0 100-2 1 1 0 000 2z"
                          />
                        </svg>
                        技術詳細
                      </TabButton>
                    </TabList>
                  </TabContainer>

                  <TabContent singleColumn={hideVideoSection}>
                    {activeTab === "overview" ? (
                      <>
                        {/* 実施手順と期待値 */}
                        <StepInfoSection>
                          <StepInfoTitle>
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 16 16"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M1.5 2.75a.25.25 0 01.25-.25h8.5a.25.25 0 01.25.25v5.5a.25.25 0 01-.25.25h-8.5a.25.25 0 01-.25-.25v-5.5zM1.75 1A1.75 1.75 0 000 2.75v5.5C0 9.216.784 10 1.75 10h8.5A1.75 1.75 0 0012 8.25v-5.5A1.75 1.75 0 0010.25 1h-8.5zM3 4.25a.75.75 0 01.75-.75h4.5a.75.75 0 010 1.5h-4.5A.75.75 0 013 4.25zm0 2a.75.75 0 01.75-.75h2a.75.75 0 010 1.5h-2A.75.75 0 013 6.25zm9.5 2.5v2.25c0 .966-.784 1.75-1.75 1.75h-8.5A1.75 1.75 0 010 10.25v-.5a.75.75 0 011.5 0v.5a.25.25 0 00.25.25h8.5a.25.25 0 00.25-.25v-2.25a.75.75 0 011.5 0z"
                              />
                            </svg>
                            ステップ詳細
                          </StepInfoTitle>
                          <StepInfoGrid>
                            <StepInfoItem>
                              <StepInfoLabel>実施手順</StepInfoLabel>
                              <StepInfoText>
                                {selectedStep.action || "手順情報がありません"}
                              </StepInfoText>
                            </StepInfoItem>
                            {selectedStep.item && (
                              <StepInfoItem>
                                <StepInfoLabel>期待値</StepInfoLabel>
                                <StepInfoText>
                                  {selectedStep.item.description ||
                                    "期待値情報がありません"}
                                </StepInfoText>
                              </StepInfoItem>
                            )}
                          </StepInfoGrid>
                        </StepInfoSection>

                        {/* スコア、理由、スコア理由の重要情報 */}
                        {(selectedStep.score !== undefined ||
                          selectedStep.reason ||
                          selectedStep.scoreReason) && (
                          <MainScoreSection>
                            <ScoreHeader>
                              <ResultBadge
                                result={
                                  selectedStep.result === "success"
                                    ? "success"
                                    : selectedStep.result === "warning"
                                    ? "warning"
                                    : selectedStep.result === "not_evaluated"
                                    ? "not_evaluated"
                                    : "failure"
                                }
                              >
                                {selectedStep.result === "success"
                                  ? "OK"
                                  : selectedStep.result === "warning"
                                  ? "要確認"
                                  : selectedStep.result === "not_evaluated"
                                  ? "未評価"
                                  : "NG"}
                              </ResultBadge>
                              {selectedStep.score !== undefined && (
                                <ScoreBadge>
                                  <ScoreLabel>信頼度</ScoreLabel>
                                  <span>
                                    {formatScore(selectedStep.score)}%
                                  </span>
                                </ScoreBadge>
                              )}
                              <ScoreTitle>評価結果</ScoreTitle>
                            </ScoreHeader>
                            <ReasonSection>
                              {selectedStep.reason && (
                                <ReasonItem>
                                  <ReasonLabel>判定理由</ReasonLabel>
                                  <ReasonText>{selectedStep.reason}</ReasonText>
                                </ReasonItem>
                              )}
                              {selectedStep.scoreReason && (
                                <ReasonItem>
                                  <ReasonLabel>スコア算出理由</ReasonLabel>
                                  <ReasonText>
                                    {selectedStep.scoreReason}
                                  </ReasonText>
                                </ReasonItem>
                              )}
                            </ReasonSection>
                          </MainScoreSection>
                        )}

                        {/* タグ分析情報 */}
                        {(() => {
                          const existingTagAnalysis =
                            testResult.value?.tag_analysis || {};
                          const aiJudgmentStatusId =
                            existingTagAnalysis.ai_judgment ||
                            apiTestResult?.scriptTestRunResult
                              ?.test_result_status_id ||
                            "";

                          // ステータスIDから表示名を取得
                          const getAiJudgmentDisplayName = (): string => {
                            if (!aiJudgmentStatusId) return '未判定';

                            // templateとgetStatusDisplayNameを使用して表示名を取得
                            if (template) {
                              try {
                                const { getStatusDisplayName } = require("../services/api");
                                return getStatusDisplayName(template, aiJudgmentStatusId);
                              } catch {
                                // フォールバック
                              }
                            }

                            // projectStatusIdsを使用してマッピング
                            if (projectStatusIds) {
                              if (aiJudgmentStatusId === projectStatusIds.OK) return 'OK';
                              if (aiJudgmentStatusId === projectStatusIds.NG) return 'NG';
                              if (aiJudgmentStatusId === projectStatusIds.AI_OK) return 'OK';
                              if (aiJudgmentStatusId === projectStatusIds.AI_PROCEDURE_NG) return '手順NG';
                              if (aiJudgmentStatusId === projectStatusIds.AI_RESULT_NG) return '結果NG';
                              if (aiJudgmentStatusId === projectStatusIds.AI_OK_NEED_REVIEW) return 'OK(要確認)';
                              if (aiJudgmentStatusId === projectStatusIds.AI_PROCEDURE_NG_NEED_REVIEW) return '手順NG(要確認)';
                              if (aiJudgmentStatusId === projectStatusIds.AI_RESULT_NG_NEED_REVIEW) return '結果NG(要確認)';
                            }

                            // 最終フォールバック
                            return aiJudgmentStatusId;
                          };

                          const aiJudgment = getAiJudgmentDisplayName();
                          const normalizeJudgment = (
                            status: string
                          ): string => {
                            if (status.includes("OK")) return "OK";
                            if (status.includes("手順NG")) return "手順NG";
                            if (status.includes("結果NG")) return "結果NG";
                            return status;
                          };
                          const judgmentMatch = humanJudgment
                            ? normalizeJudgment(aiJudgment) ===
                              normalizeJudgment(humanJudgment)
                            : false;

                          // primary_item, secondary_item, tertiary_itemは廃止
                          // action_tagsのみをevaluated_tagsから取得

                          return (
                            <div
                              style={{
                                marginTop: "6px",
                                padding: "6px",
                                background: "#f8f9fa",
                                borderRadius: "8px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "6px",
                              }}
                            >
                              {/* ステップごとの判定とAI解釈を一つのボックスに統合 */}
                              <div
                                style={{
                                  padding: "8px",
                                  background: "#fff",
                                  borderRadius: "4px",
                                  position: "sticky",
                                  top: "0",
                                  zIndex: 10,
                                }}
                              >
                                {/* 判定セクション */}
                                <div
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    marginBottom: "6px",
                                    color: "#333",
                                  }}
                                >
                                  ステップ {selectedStepIndex + 1} の判定
                                </div>

                                <div
                                  style={{
                                    display: "flex",
                                    gap: "12px",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  {/* 左側: AI判定と人間判定 */}
                                  <div style={{ flex: "1" }}>
                                    <div
                                      style={{
                                        marginBottom: "4px",
                                        fontSize: "10px",
                                        color: "#666",
                                      }}
                                    >
                                      AI判定:{" "}
                                      <strong
                                        style={{
                                          color: getJudgmentColor(
                                            getStepJudgment(selectedStep.statusId)
                                          ),
                                        }}
                                      >
                                        {getStepJudgmentDisplay(selectedStep.statusId)}
                                      </strong>
                                    </div>

                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#666",
                                        marginBottom: "3px",
                                      }}
                                    >
                                      人間判定:
                                    </div>
                                    <div
                                      style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: "4px",
                                      }}
                                    >
                                      {["OK", "結果NG", "手順NG", "未評価"].map(
                                        (judgment) => {
                                          // 現在の判定を取得（明示的に設定されている場合のみ）
                                          const currentJudgment =
                                            stepJudgments?.[
                                              selectedStepIndex + 1
                                            ];
                                          const isSelected =
                                            currentJudgment === judgment;

                                          return (
                                            <button
                                              key={judgment}
                                              onClick={() => {
                                                console.log(
                                                  `判定ボタンクリック: step=${
                                                    selectedStepIndex + 1
                                                  }, judgment=${judgment}`
                                                );
                                                if (onStepJudgmentChange) {
                                                  onStepJudgmentChange(
                                                    selectedStepIndex + 1,
                                                    judgment
                                                  );
                                                } else {
                                                  console.error(
                                                    "onStepJudgmentChange is not defined"
                                                  );
                                                }
                                              }}
                                              style={{
                                                padding: "4px 8px",
                                                borderRadius: "4px",
                                                border: isSelected
                                                  ? `2px solid ${getJudgmentColor(
                                                      judgment
                                                    )}`
                                                  : "1px solid #ccc",
                                                backgroundColor: isSelected
                                                  ? getJudgmentBackgroundColor(
                                                      judgment,
                                                      0.2
                                                    )
                                                  : "white",
                                                color:
                                                  getJudgmentColor(judgment),
                                                fontSize: "10px",
                                                fontWeight: isSelected
                                                  ? "bold"
                                                  : "normal",
                                                cursor: "pointer",
                                                minWidth: "50px",
                                                transition: "all 0.2s",
                                              }}
                                            >
                                              {judgment}
                                            </button>
                                          );
                                        }
                                      )}
                                    </div>
                                  </div>

                                  {/* 右側: AI解釈の正誤（ステップ判定が設定されている場合のみ表示） */}
                                  {(() => {
                                    const stepJudgment =
                                      stepJudgments?.[selectedStepIndex + 1];
                                    const aiStepJudgment = getStepJudgment(selectedStep.statusId);
                                    const stepJudgmentMatch =
                                      stepJudgment &&
                                      stepJudgment !== "未評価" &&
                                      stepJudgment === aiStepJudgment;

                                    if (
                                      !stepJudgment ||
                                      stepJudgment === "未評価"
                                    ) {
                                      return null;
                                    }

                                    // AI解釈の正誤を取得（ローカル状態を優先、なければ既存値を使用）
                                    const existingStepData =
                                      testResult.value?.tag_analysis?.steps?.find(
                                        (s: any) =>
                                          s.order === selectedStepIndex + 1
                                      );
                                    const stepInterpretationCorrect =
                                      stepInterpretations?.[
                                        selectedStepIndex + 1
                                      ] !== undefined
                                        ? stepInterpretations[
                                            selectedStepIndex + 1
                                          ]
                                        : existingStepData?.ai_interpretation_correct;

                                    if (!stepJudgmentMatch) {
                                      // AI判定と人間判定が不一致の場合
                                    }

                                    return (
                                      <div
                                        style={{
                                          borderLeft: "1px solid #e0e0e0",
                                          paddingLeft: "8px",
                                          minWidth: "140px",
                                        }}
                                      >
                                        <p
                                          style={{
                                            fontSize: "10px",
                                            color: "#555",
                                            marginBottom: "4px",
                                            margin: 0,
                                          }}
                                        >
                                          {stepJudgmentMatch
                                            ? "AIの解釈は正しい？"
                                            : "AI判定と異なります"}
                                        </p>
                                        {stepJudgmentMatch ? (
                                          <div
                                            style={{
                                              display: "flex",
                                              gap: "4px",
                                            }}
                                          >
                                            <button
                                              style={{
                                                padding: "2px 6px",
                                                borderRadius: "3px",
                                                fontSize: "9px",
                                                fontWeight: "500",
                                                cursor: "pointer",
                                                border: "1px solid #4caf50",
                                                background:
                                                  stepInterpretationCorrect ===
                                                  true
                                                    ? "#4caf50"
                                                    : "white",
                                                color:
                                                  stepInterpretationCorrect ===
                                                  true
                                                    ? "white"
                                                    : "#4caf50",
                                                transition: "all 0.2s",
                                              }}
                                              onClick={() => {
                                                // ステップレベルのAI解釈の正誤を更新
                                                onStepInterpretationUpdate?.(
                                                  selectedStepIndex + 1,
                                                  true
                                                );
                                              }}
                                            >
                                              正しい
                                            </button>
                                            <button
                                              style={{
                                                padding: "2px 6px",
                                                borderRadius: "3px",
                                                fontSize: "9px",
                                                fontWeight: "500",
                                                cursor: "pointer",
                                                border: "1px solid #f44336",
                                                background:
                                                  stepInterpretationCorrect ===
                                                  false
                                                    ? "#f44336"
                                                    : "white",
                                                color:
                                                  stepInterpretationCorrect ===
                                                  false
                                                    ? "white"
                                                    : "#f44336",
                                                transition: "all 0.2s",
                                              }}
                                              onClick={() => {
                                                // ステップレベルのAI解釈の正誤を更新
                                                onStepInterpretationUpdate?.(
                                                  selectedStepIndex + 1,
                                                  false
                                                );
                                              }}
                                            >
                                              誤り
                                            </button>
                                          </div>
                                        ) : (
                                          <div
                                            style={{
                                              fontSize: "9px",
                                              color: "#999",
                                            }}
                                          >
                                            AI判定と異なるため、
                                            <br />
                                            解釈の正誤は評価できません
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>

                              {/* タグ情報の表示は廃止（primary_item, secondary_item, tertiary_itemは廃止、action_tagsはai_sstf_controllerで管理） */}
                            </div>
                          );
                        })()}
                      </>
                    ) : activeTab === "details" ? (
                      /* 詳細タブ: テストターゲット情報 */
                      <TestTargetSection>
                        <TestTargetTitle>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 16 16"
                            fill="currentColor"
                          >
                            <path d="M8 1a.75.75 0 01.75.75V4h1.75a.75.75 0 010 1.5H8.75v1.75a.75.75 0 01-1.5 0V5.5H5.5a.75.75 0 010-1.5h1.75V1.75A.75.75 0 018 1z" />
                            <path
                              fillRule="evenodd"
                              d="M8 15A7 7 0 108 1a7 7 0 000 14zm0-1.5a5.5 5.5 0 100-11 5.5 5.5 0 000 11z"
                            />
                          </svg>
                          操作対象テストターゲット詳細
                        </TestTargetTitle>
                        {(() => {
                          const operationStep =
                            getOperationStep(selectedStepIndex);

                          if (!operationStep) {
                            return (
                              <div
                                style={{
                                  padding: "12px",
                                  textAlign: "center",
                                  color: "#6c757d",
                                  fontSize: "12px",
                                }}
                              >
                                操作ログ情報が取得できません
                                {loadingOperationLog && (
                                  <LoadingIndicator>
                                    操作ログを読み込み中...
                                  </LoadingIndicator>
                                )}
                              </div>
                            );
                          }

                          return (
                            <TestTargetInfo>
                              <TestTargetItem>
                                <TestTargetLabel>アクション:</TestTargetLabel>
                                <TestTargetValue>
                                  {operationStep.action}
                                </TestTargetValue>
                              </TestTargetItem>
                              <TestTargetItem>
                                <TestTargetLabel>ターゲットID:</TestTargetLabel>
                                <TestTargetValue>
                                  {operationStep.test_target_id}
                                </TestTargetValue>
                              </TestTargetItem>
                              {operationStep.item && (
                                <>
                                  <TestTargetItem>
                                    <TestTargetLabel>対象名:</TestTargetLabel>
                                    <TestTargetValue>
                                      {operationStep.item.name}
                                    </TestTargetValue>
                                  </TestTargetItem>
                                  {operationStep.item.description && (
                                    <TestTargetDescription>
                                      <strong>説明:</strong>{" "}
                                      {operationStep.item.description}
                                    </TestTargetDescription>
                                  )}
                                </>
                              )}
                              {operationStep.params &&
                                Object.keys(operationStep.params).length >
                                  0 && (
                                  <div>
                                    <TestTargetItem>
                                      <TestTargetLabel>
                                        パラメータ:
                                      </TestTargetLabel>
                                    </TestTargetItem>
                                    <TestTargetParams>
                                      {JSON.stringify(
                                        operationStep.params,
                                        null,
                                        2
                                      )}
                                    </TestTargetParams>
                                  </div>
                                )}
                              <TestTargetItem>
                                <TestTargetLabel>実行モード:</TestTargetLabel>
                                <TestTargetValue style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  {renderOperationTypeIcon(operationStep.operation_type)}
                                  {operationStep.operation_type === "auto"
                                    ? "自動実行"
                                    : operationStep.operation_type === "manual"
                                    ? "手動実行"
                                    : operationStep.operation_type}
                                </TestTargetValue>
                              </TestTargetItem>
                            </TestTargetInfo>
                          );
                        })()}
                      </TestTargetSection>
                    ) : (
                      // エビデンスタブ
                      <EvidenceSection>
                        <SectionTitle>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 16 16"
                            fill="currentColor"
                          >
                            <path d="M8 9a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                            <path
                              fillRule="evenodd"
                              d="M1.5 3a1.5 1.5 0 011.5-1.5h10A1.5 1.5 0 0114.5 3v10a1.5 1.5 0 01-1.5 1.5H3A1.5 1.5 0 011.5 13V3zM3 3v10h10V3H3z"
                            />
                          </svg>
                          エビデンス画像
                        </SectionTitle>
                        {(() => {
                          const operationStep =
                            getOperationStep(selectedStepIndex);
                          const isRegression = isRegressionTest(
                            evidenceFiles,
                            regressionBaseline,
                          );

                          if (isRegression) {
                            const scoreReason =
                              testResult?.steps?.[selectedStepIndex]
                                ?.scoreReason;
                            // teststep.order はシナリオグループ内 TC で
                            // グループ通し番号 (例: 2 番目 TC で 3,4) になる。
                            // リグレッション画像 (`expected_step_N.jpg`) や
                            // `regression_baseline` のキーが teststep.order
                            // で揃うため、位置 index ではなく原本の order を
                            // 渡す。未伝搬の古いデータでは位置 index に fall back。
                            const regressionStepOrder =
                              testResult?.steps?.[selectedStepIndex]?.order ??
                              selectedStepIndex + 1;
                            return (
                              <RegressionDiffView
                                stepOrder={regressionStepOrder}
                                evidenceFiles={evidenceFiles || {}}
                                operationStep={operationStep ?? undefined}
                                scoreReason={scoreReason}
                                testTargets={testTargets}
                                allOperationSteps={operationLog?.steps}
                                baselineUrls={baselineUrls}
                                regressionBaseline={regressionBaseline}
                                regressionThresholds={regressionThresholds}
                              />
                            );
                          }

                          const evidence = operationStep?.evidence
                            ? (evidenceFiles || {})[operationStep.evidence]
                            : undefined;
                          return evidence ? (
                            <EvidenceGrid>
                              <EvidenceItem
                                onClick={() => evidence && openModal(evidence)}
                              >
                                <EvidenceImage
                                  src={evidence}
                                  alt="Evidence"
                                  onError={(e) =>
                                    evidence && handleImageError(evidence, e)
                                  }
                                />
                                <EvidenceLabel>
                                  <span>
                                    ステップ {selectedStepIndex + 1}{" "}
                                    のエビデンス
                                  </span>
                                  <EvidenceIcon>
                                    <svg
                                      width="12"
                                      height="12"
                                      viewBox="0 0 16 16"
                                      fill="currentColor"
                                    >
                                      <path d="M11.742 10.344a6.5 6.5 0 10-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 001.415-1.414l-3.85-3.85a1.007 1.007 0 00-.115-.1zM12 6.5a5.5 5.5 0 11-11 0 5.5 5.5 0 0111 0z" />
                                    </svg>
                                  </EvidenceIcon>
                                </EvidenceLabel>
                              </EvidenceItem>
                              {loadingEvidenceFiles && (
                                <LoadingIndicator>
                                  エビデンスファイルを読み込み中...
                                </LoadingIndicator>
                              )}
                            </EvidenceGrid>
                          ) : (
                            <NoEvidenceContainer>
                              <NoEvidenceIcon>
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="currentColor"
                                >
                                  <path d="M10.5 8.5a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                                  <path d="M2 4a2 2 0 00-2 2v6a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1.172a2 2 0 01-1.414-.586L9.828 1.828A2 2 0 008.414 1H7.586a2 2 0 00-1.414.586L4.586 3.414A2 2 0 013.172 4H2zm.5 2a.5.5 0 11-1 0 .5.5 0 011 0zm9 2.5a3.5 3.5 0 11-7 0 3.5 3.5 0 017 0z" />
                                </svg>
                              </NoEvidenceIcon>
                              <NoEvidenceText>
                                このステップにはエビデンス画像がありません
                              </NoEvidenceText>
                              {(loadingOperationLog ||
                                loadingEvidenceFiles) && (
                                <NoEvidenceSubText>
                                  {loadingOperationLog &&
                                    "操作ログを読み込み中..."}
                                  {loadingOperationLog &&
                                    loadingEvidenceFiles && <br />}
                                  {loadingEvidenceFiles &&
                                    "エビデンスファイルを読み込み中..."}
                                </NoEvidenceSubText>
                              )}
                            </NoEvidenceContainer>
                          );
                        })()}
                      </EvidenceSection>
                    )}
                  </TabContent>
                </RightPanel>
              </ContentGrid>
                );
              })()}
            </EvidenceContent>
          </>
        ) : (
          <EmptyState>ステップを選択してください</EmptyState>
        )}
      </EvidencePanel>

      <ZoomableImageModal
        isOpen={!!modalImage}
        imageUrl={modalImage}
        onClose={closeModal}
        alt="Evidence Detail"
      />
    </ViewerContainer>
  );
};

export default EvidenceViewer;
